import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { MapPin } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { LatLngExpression } from 'leaflet'
import { useAccounts } from '@/lib/use-accounts'
import {
  useSearchFilters,
  useSearchStatus,
  useStartSearch,
  useStopSearch,
} from '@/lib/api'
import { useFeatureFlag } from '@/lib/use-feature-flags'
import { useFavoriteLocations } from '@/lib/api/queries/favorite-locations'
import { Button } from '@/components/ui/button'
import { ErrorBoundary } from '@/components/error-boundary'
import {
  AccountSelector,
  FilterPanel,
  MapSection,
  RadiusControl,
  SearchActionBar,
} from '@/components/search'

// Montreal coordinates
const MONTREAL_CENTER: LatLngExpression = [45.5017, -73.5673]

// Search request type
export interface SearchRequest {
  id: string
  accountId: string
  accountName: string
  center: LatLngExpression
  radius: number // in meters
  filters: Record<string, boolean>
  status: 'pending' | 'active' | 'completed' | 'cancelled'
  createdAt: Date
  resultsCount?: number
}

export const Route = createFileRoute('/search')({
  component: SearchPage,
})

function SearchPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { accounts: accountsList, isLoading: accountsLoading } = useAccounts()
  const [userLocation, setUserLocation] = useState<LatLngExpression | null>(
    null,
  )
  const [searchCenter, setSearchCenter] =
    useState<LatLngExpression>(MONTREAL_CENTER)
  const [searchRadius, setSearchRadius] = useState(300) // 300m default
  const [showManualInput, setShowManualInput] = useState(false)
  const [manualRadiusValue, setManualRadiusValue] = useState('300')
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  )
  const [showFilters, setShowFilters] = useState(false)
  const [showAccountSelection, setShowAccountSelection] = useState(false)
  const [filters, setFilters] = useState<Record<string, boolean>>({})
  const [searchHistory, setSearchHistory] = useState<Array<SearchRequest>>([])
  const [activeSearchAccountId, setActiveSearchAccountId] = useState<
    string | null
  >(null)

  // Feature flags and favorite locations
  const [favoriteLocationsEnabled] = useFeatureFlag('analytics')
  const { data: favoriteLocationsData } = useFavoriteLocations(
    favoriteLocationsEnabled,
  )

  // API hooks
  const startSearchMutation = useStartSearch()
  const stopSearchMutation = useStopSearch()
  const { data: availableFilters, isLoading: filtersLoading } =
    useSearchFilters()

  // Initialize filters with default values when available filters are loaded
  useEffect(() => {
    if (availableFilters?.filters && Object.keys(filters).length === 0) {
      const defaultFilters: Record<string, boolean> = {}
      availableFilters.filters.forEach((filter) => {
        defaultFilters[filter.name] = filter.default
      })
      setFilters(defaultFilters)
    }
  }, [availableFilters, filters])

  // Poll search status while there's an active search
  const { data: searchStatus } = useSearchStatus(
    activeSearchAccountId ?? undefined,
    !!activeSearchAccountId,
  )

  // Watch for search completion
  useEffect(() => {
    if (searchStatus && activeSearchAccountId) {
      const status = searchStatus.status.toLowerCase()
      if (
        status === 'completed' ||
        status === 'stopped' ||
        status === 'error'
      ) {
        // Update search history with result
        setSearchHistory((prev) =>
          prev.map((s) =>
            s.accountId === activeSearchAccountId && s.status === 'active'
              ? {
                  ...s,
                  status:
                    status === 'completed'
                      ? 'completed'
                      : status === 'error'
                        ? 'cancelled'
                        : 'completed',
                  resultsCount: searchStatus.result?.is_success ? 1 : 0,
                }
              : s,
          ),
        )

        // Show result notification
        if (searchStatus.result?.is_success) {
          toast.success(t('search.searchSuccess') || 'Car found and reserved!')
        } else if (status === 'error' && searchStatus.result?.error) {
          toast.error(searchStatus.result.error)
        }

        setActiveSearchAccountId(null)
      }
    }
  }, [searchStatus, activeSearchAccountId, t])

  const isSearching =
    startSearchMutation.isPending ||
    stopSearchMutation.isPending ||
    !!activeSearchAccountId

  // Get user location on mount
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        const location: LatLngExpression = [latitude, longitude]
        setUserLocation(location)
        setSearchCenter(location)
      },
      (error) => {
        console.error('Error getting user location:', error)
        setSearchCenter(MONTREAL_CENTER)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      },
    )
  }, [])

  const handleGoToUserLocation = () => {
    if (userLocation) {
      setSearchCenter(userLocation)
    } else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          const location: LatLngExpression = [latitude, longitude]
          setUserLocation(location)
          setSearchCenter(location)
        },
        (_error) => {
          toast.error(t('search.unableToGetLocation'))
        },
      )
    }
  }

  const handleStartSearch = async () => {
    if (!selectedAccountId) {
      toast.warning(t('search.pleaseSelectAccount'))
      return
    }

    const account = accountsList.find((a) => a.id === selectedAccountId)
    if (!account) return

    // Get center coordinates
    const centerArray = Array.isArray(searchCenter)
      ? searchCenter
      : [searchCenter.lat, searchCenter.lng]

    try {
      // Start the search via API
      await startSearchMutation.mutateAsync({
        accountName: selectedAccountId,
        params: {
          point: {
            latitude: centerArray[0],
            longitude: centerArray[1],
          },
          radius: searchRadius / 1000, // Convert meters to km
          filters: filters, // Send filters as object
        },
      })

      // Add to local search history
      const newSearch: SearchRequest = {
        id: Date.now().toString(),
        accountId: selectedAccountId,
        accountName: account.name,
        center: searchCenter,
        radius: searchRadius,
        filters: { ...filters },
        status: 'active',
        createdAt: new Date(),
      }

      setSearchHistory([newSearch, ...searchHistory])
      setActiveSearchAccountId(selectedAccountId)

      // Navigate to search status page
      navigate({ to: '/search/status' })
    } catch (error) {
      console.error('Failed to start search:', error)
      toast.error(
        t('search.searchFailed') || 'Failed to start search. Please try again.',
      )
    }
  }

  const handleStopSearch = async () => {
    if (!activeSearchAccountId) return

    try {
      await stopSearchMutation.mutateAsync({
        accountName: activeSearchAccountId,
      })

      // Update search history
      setSearchHistory((prev) =>
        prev.map((s) =>
          s.accountId === activeSearchAccountId && s.status === 'active'
            ? { ...s, status: 'cancelled' }
            : s,
        ),
      )

      setActiveSearchAccountId(null)
    } catch (error) {
      console.error('Failed to stop search:', error)
      toast.error(t('search.stopSearchFailed') || 'Failed to stop search.')
    }
  }

  const handleReset = () => {
    setSelectedAccountId(null)
    // Reset filters to defaults
    if (availableFilters?.filters) {
      const defaultFilters: Record<string, boolean> = {}
      availableFilters.filters.forEach((filter) => {
        defaultFilters[filter.name] = filter.default
      })
      setFilters(defaultFilters)
    }
    setSearchRadius(1000)
    setManualRadiusValue('1000')
    if (userLocation) {
      setSearchCenter(userLocation)
    } else {
      setSearchCenter(MONTREAL_CENTER)
    }
  }

  const getActiveFiltersCount = () => {
    if (!availableFilters?.filters) return 0
    return Object.entries(filters).filter(([name, enabled]) => {
      const filterDef = availableFilters.filters.find((f) => f.name === name)
      return filterDef && enabled !== filterDef.default
    }).length
  }

  return (
    <div className="fixed-fullscreen-safe fixed flex flex-col">
      {/* Map */}
      <ErrorBoundary
        fallback={
          <div className="flex-1 flex items-center justify-center bg-muted">
            <p className="text-muted-foreground">Failed to load map</p>
          </div>
        }
      >
        <MapSection
          searchCenter={searchCenter}
          searchRadius={searchRadius}
          onCenterChange={setSearchCenter}
          onGoToUserLocation={handleGoToUserLocation}
          onToggleFilters={() => setShowFilters(!showFilters)}
        />
      </ErrorBoundary>

      {/* Compact Bottom Panel - Focused on Search Center and Radius */}
      <div className="bg-background/95 backdrop-blur border-t border-border">
        <div className="container mx-auto max-w-screen-md px-4 py-3">
          {/* Favorite Location Quick Access */}
          {favoriteLocationsEnabled &&
            favoriteLocationsData &&
            favoriteLocationsData.locations.length > 0 && (
              <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
                {favoriteLocationsData.locations.map((loc) => (
                  <Button
                    key={loc.id}
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5"
                    onClick={() => {
                      setSearchCenter([loc.latitude, loc.longitude])
                      setSearchRadius(loc.radius * 1000)
                      setManualRadiusValue(String(loc.radius * 1000))
                      if (loc.filters) {
                        setFilters((prev) => ({ ...prev, ...loc.filters }))
                      }
                    }}
                  >
                    <MapPin className="h-3.5 w-3.5" />
                    {loc.name}
                  </Button>
                ))}
              </div>
            )}

          <RadiusControl
            searchRadius={searchRadius}
            showManualInput={showManualInput}
            manualRadiusValue={manualRadiusValue}
            onRadiusChange={setSearchRadius}
            onManualValueChange={setManualRadiusValue}
            onShowManualInput={setShowManualInput}
          />

          <SearchActionBar
            selectedAccountId={selectedAccountId}
            selectedAccountName={
              accountsList.find((a) => a.id === selectedAccountId)?.name
            }
            isSearching={isSearching}
            activeFiltersCount={getActiveFiltersCount()}
            isStartPending={startSearchMutation.isPending}
            isStopPending={stopSearchMutation.isPending}
            onStartSearch={handleStartSearch}
            onStopSearch={handleStopSearch}
            onReset={handleReset}
            onShowAccountSelection={() => setShowAccountSelection(true)}
            onToggleFilters={() => setShowFilters(!showFilters)}
            onNavigateToStatus={() => navigate({ to: '/search/status' })}
          />
        </div>
      </div>

      {/* Account Selection Panel */}
      <AccountSelector
        show={showAccountSelection}
        accounts={accountsList}
        accountsLoading={accountsLoading}
        selectedAccountId={selectedAccountId}
        onClose={() => setShowAccountSelection(false)}
        onSelectAccount={setSelectedAccountId}
      />

      {/* Filters Panel */}
      <FilterPanel
        show={showFilters}
        filters={filters}
        availableFilters={availableFilters?.filters}
        filtersLoading={filtersLoading}
        onClose={() => setShowFilters(false)}
        onFilterChange={(name, checked) =>
          setFilters({ ...filters, [name]: checked })
        }
      />
    </div>
  )
}
